import { atom } from "recoil";
import { selector } from "./recoilSelectors";
import { DetailedLocation, LonLat } from "../types/core";
import { placesSelectedUidsState } from "./placeState";

export type DrawMode = {
    type: "zone" | "route" | "place" | "job" | "team" | "attachment" | "start" | "end" | "company" | "trip";
    action: "new" | "edit";
    location?: DetailedLocation;
    radius?: number;
    coordinates?: LonLat[];
    /** a New job, route, place or zone card owns this: no navbar search, and the map sets the location only while picking */
    composer?: "job" | "route" | "place" | "zone";
    pickOnMap?: boolean;
    /** the New zone card: an outline to start from (around a point or around places), edited by its corners */
    seed?: LonLat[];
    /** the New zone card: bumped when the shape starts over, so the drawing restarts once */
    shapeVersion?: number;
}

export function isLocationDrawMode(drawMode?: DrawMode) {
    return drawMode?.type === "job"
        || drawMode?.type === "place"
        || drawMode?.type === "team"
        || drawMode?.type === "start"
        || drawMode?.type === "end"
        || drawMode?.type === "attachment"
        || drawMode?.type === "company"
        || drawMode?.type === "trip";
}

/** The trip editor's endpoint being picked on the map; `confirmed` carries the pick back once OK is pressed. */
export type TripEndpointPick = { which: "start" | "end"; confirmed?: DetailedLocation };
export const tripEndpointPickState = atom<TripEndpointPick | undefined>({
    key: 'map.draw.trip.endpoint',
    default: undefined,
});

export const drawModeState = atom<DrawMode | undefined>({
    key: 'map.draw.mode',
    default: undefined,
});
export const locationDraggingState = atom({
    key: 'map.draw.location.dragging',
    default: false,
});
export const allowBulkDrawPlacesState = selector({
    key: 'map.draw.places.bulk.allow',
    get: ({ get }) => {
        const selectionCount = get(placesSelectedUidsState).length;
        return !!selectionCount && selectionCount <= 10;
    }
});
export const bulkDrawPlacesState = atom({
    key: 'map.draw.places.bulk',
    default: false,
});

/** A click on the map sets the location: every location mode, the New job card only while it asks for one. */
export function isPickingLocationOnMap(drawMode?: DrawMode) {
    return isLocationDrawMode(drawMode) && (!drawMode?.composer || !!drawMode.pickOnMap);
}

/** The New job card is open: the header, the map search and the map tools step aside for it. */
export const jobComposerOpenState = selector({
    key: 'map.draw.composer.open',
    get: ({ get }) => !!get(drawModeState)?.composer,
});
